import React, { useEffect, useRef, useState } from 'react';

const clampAngleCosine = (value) => Math.min(1, Math.max(-1, value));

const buildShoulderAngleGuide = ({
  origin,
  target,
  horizontalDirection = 'right',
  radius = 0.9,
}) => {
  if (!origin || !target) return null;
  const shoulderVector = {
    x: (target.x || 0) - (origin.x || 0),
    y: -((target.y || 0) - (origin.y || 0)),
  };
  const shoulderLength = Math.hypot(shoulderVector.x, shoulderVector.y);
  if (shoulderLength < 1e-8) return null;

  const baseVector = horizontalDirection === 'left'
    ? { x: -1, y: 0 }
    : { x: 1, y: 0 };
  const normalizedShoulder = {
    x: shoulderVector.x / shoulderLength,
    y: shoulderVector.y / shoulderLength,
  };
  const angleDegrees = (Math.acos(clampAngleCosine(
    (baseVector.x * normalizedShoulder.x) + (baseVector.y * normalizedShoulder.y),
  )) * 180) / Math.PI;

  const originSvg = { x: origin.x || 0, y: -(origin.y || 0) };
  const baseEnd = {
    x: originSvg.x + (baseVector.x * radius),
    y: originSvg.y + (baseVector.y * radius),
  };
  const shoulderEnd = {
    x: originSvg.x + (normalizedShoulder.x * radius),
    y: originSvg.y + (normalizedShoulder.y * radius),
  };
  const labelPosition = {
    x: originSvg.x + ((baseVector.x + normalizedShoulder.x) * radius * 0.78),
    y: originSvg.y + ((baseVector.y + normalizedShoulder.y) * radius * 0.78) - 0.12,
  };

  return {
    angleDegrees,
    originSvg,
    baseEnd,
    shoulderEnd,
    labelPosition,
  };
};

export default function Canvas({ 
  patternData, 
  comparisonPatterns = [],
  viewportResetToken = 0,
  measurements = {},
  zoom, 
  setZoom,
  showFront = true,
  showSide = true,
  showBack = true,
  showYoke = true,
  showCollar = false,
  showSleeve = true,
  showMeasurements = true,
  showPoints = false,
  seamWalkMode = false,
  sleeveOnBodyMode = false,
  selectedPiece = null,
  onPieceClick,
  selectedCurveId = null,
  onCurveSelect,
  canEdit = true,
  onCurveHandleChange,
  freeModeActive = false,
  selectedFreePointNames = [],
  onFreePointSelectionChange,
}) {
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [dragStart, setDragStart] = useState(null);
  const [isSpacePressed, setIsSpacePressed] = useState(false);
  const [isCurveDragging, setIsCurveDragging] = useState(false);
  const [activeCurveHandleId, setActiveCurveHandleId] = useState(null);
  const [hoveredPointKey, setHoveredPointKey] = useState(null);
  const [pinnedPointKey, setPinnedPointKey] = useState(null);
  const [isLassoSelecting, setIsLassoSelecting] = useState(false);
  const [lassoStart, setLassoStart] = useState(null);
  const [lassoCurrent, setLassoCurrent] = useState(null);
  const canvasRef = useRef(null);
  const svgRef = useRef(null);
  const curveDragCleanupRef = useRef(null);
  const hasRenderablePattern = Boolean(
    patternData?.frontPath
    || patternData?.sidePath
    || patternData?.backPath
    || patternData?.yokePath
    || patternData?.sleevePath
    || patternData?.topSleevePath
    || patternData?.underSleevePath
  );

  const toStep = (value, step = 0.0625) => Math.round(value / step) * step;

  const getSvgCoordinates = (clientX, clientY) => {
    if (!svgRef.current) return null;
    const svg = svgRef.current;
    const point = svg.createSVGPoint();
    point.x = clientX;
    point.y = clientY;
    const ctm = svg.getScreenCTM();
    if (!ctm) return null;
    const transformed = point.matrixTransform(ctm.inverse());
    return { x: transformed.x, y: transformed.y };
  };

  const applyCurveHandleDelta = (clientX, clientY, dragState) => {
    if (!dragState) return;
    const svgPoint = getSvgCoordinates(clientX, clientY);
    if (!svgPoint) return;

    const deltaX = svgPoint.x - dragState.startPoint.x;
    const deltaY = svgPoint.y - dragState.startPoint.y;

    const constrainedDeltaX = dragState.lockAxis === 'y' ? 0 : deltaX;
    const constrainedDeltaY = dragState.lockAxis === 'x' ? 0 : deltaY;
    const hasLayoutMap = Number.isFinite(Number(dragState.layoutCos)) && Number.isFinite(Number(dragState.layoutSin));
    const mappedDeltaX = hasLayoutMap
      ? (constrainedDeltaX * dragState.layoutCos) + (constrainedDeltaY * dragState.layoutSin)
      : constrainedDeltaX;
    const mappedDeltaY = hasLayoutMap
      ? (-constrainedDeltaX * dragState.layoutSin) + (constrainedDeltaY * dragState.layoutCos)
      : constrainedDeltaY;

    const nextX = toStep(dragState.startDx + mappedDeltaX);
    const nextY = toStep(dragState.startDy + mappedDeltaY);

    const payload = {};
    if (dragState.xKey) payload[dragState.xKey] = nextX;
    if (dragState.yKey) payload[dragState.yKey] = nextY;

    onCurveHandleChange?.(payload);
  };

  useEffect(() => () => {
    if (curveDragCleanupRef.current) {
      curveDragCleanupRef.current();
      curveDragCleanupRef.current = null;
    }
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.code === 'Space') {
        setIsSpacePressed(true);
      }
    };

    const handleKeyUp = (event) => {
      if (event.code === 'Space') {
        setIsSpacePressed(false);
      }
    };

    const handleWindowBlur = () => {
      setIsSpacePressed(false);
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('blur', handleWindowBlur);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('blur', handleWindowBlur);
    };
  }, []);

  useEffect(() => {
    setActiveCurveHandleId(null);
  }, [patternData?.garmentType, patternData?.formulaVersion, selectedCurveId]);

  useEffect(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
    setDragStart(null);
  }, [viewportResetToken, setZoom]);

  const handleMouseDown = (e) => {
    const isMiddlePan = e.button === 1;
    const isSpaceLeftPan = e.button === 0 && isSpacePressed;
    const targetTestId = typeof e.target?.getAttribute === 'function'
      ? String(e.target.getAttribute('data-testid') || '')
      : '';
    const isInteractiveCanvasTarget = targetTestId.includes('-point-hitbox-')
      || targetTestId.includes('-curve-segment-hitbox-')
      || targetTestId.includes('-curve-handle-hitbox-');
    const canUseLeftLasso = freeModeActive
      && (showPoints || freeModeActive)
      && e.button === 0
      && !isSpacePressed
      && !isInteractiveCanvasTarget;

    if (isMiddlePan || isSpaceLeftPan) {
      e.preventDefault();
      if (isCurveDragging) return;
      setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
      return;
    }

    if (canUseLeftLasso && canEdit) {
      e.preventDefault();
      const svgPoint = getSvgCoordinates(e.clientX, e.clientY);
      if (svgPoint) {
        setIsLassoSelecting(true);
        setLassoStart(svgPoint);
        setLassoCurrent(svgPoint);
      }
      return;
    }

    if (isCurveDragging) return;
    if (e.button === 0) {
      setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
    }
  };

  const handleMouseMove = (e) => {
    if (isLassoSelecting) {
      const svgPoint = getSvgCoordinates(e.clientX, e.clientY);
      if (svgPoint) {
        setLassoCurrent(svgPoint);
      }
      return;
    }

    if (isCurveDragging) {
      return;
    }

    if (dragStart) {
      setPan({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
    }
  };

  const handleMouseUp = () => {
    if (isLassoSelecting && canEdit && lassoStart && lassoCurrent && freeModeActive && (showPoints || freeModeActive)) {
      const minX = Math.min(lassoStart.x, lassoCurrent.x);
      const maxX = Math.max(lassoStart.x, lassoCurrent.x);
      const minY = Math.min(lassoStart.y, lassoCurrent.y);
      const maxY = Math.max(lassoStart.y, lassoCurrent.y);
      const lassoWidth = Math.abs(maxX - minX);
      const lassoHeight = Math.abs(maxY - minY);

      if (lassoWidth > 0.2 || lassoHeight > 0.2) {
        const selectedByLasso = visibleNamedPointsList
          .filter((point) => isPieceVisible(point.piece))
          .filter((point) => point?.selectable !== false)
          .filter((point) => {
            const ySvg = -point.y;
            return point.x >= minX && point.x <= maxX && ySvg >= minY && ySvg <= maxY;
          })
          .map((point) => point.name);

        onFreePointSelectionChange?.(selectedByLasso);
      }
    }

    setIsLassoSelecting(false);
    setLassoStart(null);
    setLassoCurrent(null);

    setDragStart(null);
    if (curveDragCleanupRef.current) {
      curveDragCleanupRef.current();
      curveDragCleanupRef.current = null;
    }
    setIsCurveDragging(false);
  };

  const handleCurveHandleMouseDown = (event, handle) => {
    if (!canEdit) return;
    event.stopPropagation();
    event.preventDefault();

    if (selectedCurveId !== handle.curveId) {
      onCurveSelect?.(handle.curveId);
    }

    if (activeCurveHandleId !== handle.id) {
      setActiveCurveHandleId(handle.id);
      return;
    }

    const svgPoint = getSvgCoordinates(event.clientX, event.clientY);
    if (!svgPoint) return;

    const nextState = {
      xKey: handle.xKey,
      yKey: handle.yKey,
      lockAxis: handle.lockAxis,
      startPoint: svgPoint,
      startDx: Number(measurements?.[handle.xKey]) || 0,
      startDy: Number(measurements?.[handle.yKey]) || 0,
      layoutCos: handle.layoutCos,
      layoutSin: handle.layoutSin,
    };

    setIsCurveDragging(true);

    if (curveDragCleanupRef.current) {
      curveDragCleanupRef.current();
      curveDragCleanupRef.current = null;
    }

    const moveHandler = (moveEvent) => {
      applyCurveHandleDelta(moveEvent.clientX, moveEvent.clientY, nextState);
    };

    const upHandler = () => {
      window.removeEventListener('mousemove', moveHandler);
      window.removeEventListener('mouseup', upHandler);
      curveDragCleanupRef.current = null;
      setIsCurveDragging(false);
    };

    window.addEventListener('mousemove', moveHandler);
    window.addEventListener('mouseup', upHandler);
    curveDragCleanupRef.current = () => {
      window.removeEventListener('mousemove', moveHandler);
      window.removeEventListener('mouseup', upHandler);
    };
  };

  const handleCurveSegmentSelect = (event, curveId) => {
    if (!canEdit) return;
    event.stopPropagation();
    onCurveSelect?.(curveId);
    setActiveCurveHandleId(null);
  };

  const clearCurveSelection = (event) => {
    if (event.target === event.currentTarget) {
      onCurveSelect?.(null);
      setActiveCurveHandleId(null);
      setHoveredPointKey(null);
      setPinnedPointKey(null);
      if (freeModeActive && !isLassoSelecting) {
        onFreePointSelectionChange?.([]);
      }
    }
  };

  const handleWheel = (e) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      const delta = e.deltaY > 0 ? 0.9 : 1.1;
      setZoom(prev => Math.min(Math.max(prev * delta, 0.2), 10));
    } else {
      // Regular scroll zoom to cursor position
      e.preventDefault();
      const rect = canvasRef.current.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;
      
      const delta = e.deltaY > 0 ? 0.95 : 1.05;
      const newZoom = Math.min(Math.max(zoom * delta, 0.2), 10);
      
      // Adjust pan to zoom toward cursor
      const zoomFactor = newZoom / zoom;
      setPan({
        x: mouseX - (mouseX - pan.x) * zoomFactor,
        y: mouseY - (mouseY - pan.y) * zoomFactor
      });
      
      setZoom(newZoom);
    }
  };

  const handleFit = () => {
    setZoom(1.8);
    setPan({ x: 200, y: 400 });
  };

  const isFrontSelected = selectedPiece === 'front';
  const isSideSelected = selectedPiece === 'side';
  const isBackSelected = selectedPiece === 'back';
  const isYokeSelected = selectedPiece === 'yoke';
  const isCollarSelected = selectedPiece === 'collar';
  const isSleeveSelected = selectedPiece === 'sleeve';
  const hasJacketSleeves = Boolean(patternData?.garmentType === 'jacket' && (patternData?.topSleevePath || patternData?.underSleevePath));

  const namedPointsList = Array.isArray(patternData?.namedPoints) ? patternData.namedPoints : [];
  const peakLapelGuidePoint = (() => {
    if (patternData?.garmentType !== 'jacket' || patternData?.lapelSummary?.lapelType !== 'peak') return null;
    const rollGuide = patternData?.rollLineGuide;
    const gorgePointDepth = Number(patternData?.lapelSummary?.gorgePointDepth || 0);
    if (!rollGuide?.p1 || !rollGuide?.p2 || !Number.isFinite(gorgePointDepth)) return null;

    const dx = rollGuide.p2.x - rollGuide.p1.x;
    const dy = rollGuide.p2.y - rollGuide.p1.y;
    const mag = Math.hypot(dx, dy);
    if (mag < 1e-8) return null;

    return {
      name: 'LP1',
      x: rollGuide.p1.x + ((dx / mag) * gorgePointDepth),
      y: rollGuide.p1.y + ((dy / mag) * gorgePointDepth),
    };
  })();
  const hiddenJacketPointNames = patternData?.garmentType === 'jacket'
    ? new Set(['RN1', 'RN2', 'RN1_M', 'RN2_M', 'CP1_M', 'CP2_M'])
    : new Set();
  const hiddenJacketLapelConstructionLineIds = patternData?.garmentType === 'jacket'
    && String(patternData?.lapelSummary?.lapelType || '').toLowerCase() === 'rounded_notch'
    ? new Set(['lapel-gp-cp0', 'lapel-gp-cp0-mirror'])
    : new Set();
  const visibleNamedPointsList = namedPointsList.filter((point) => !hiddenJacketPointNames.has(point?.name));
  const namedPointByKey = visibleNamedPointsList.reduce((acc, point) => {
    acc[`${point.piece}-${point.name}`] = point;
    return acc;
  }, {});
  const namedPointByName = namedPointsList.reduce((acc, point) => {
    acc[point.name] = point;
    return acc;
  }, {});
  const shirtBackShoulderAngleGuide = patternData?.garmentType === 'shirt'
    ? buildShoulderAngleGuide({
        origin: namedPointByName.BHPS,
        target: namedPointByName.BLPS,
        horizontalDirection: 'right',
      })
    : null;
  const shirtFrontShoulderAngleGuide = patternData?.garmentType === 'shirt'
    ? buildShoulderAngleGuide({
        origin: namedPointByName.FHPS,
        target: namedPointByName.FLPS,
        horizontalDirection: 'left',
      })
    : null;
  const effectiveShowPoints = showPoints || freeModeActive;
  const showPointLabels = showPoints || !freeModeActive;
  const hoveredPoint = effectiveShowPoints && hoveredPointKey ? namedPointByKey[hoveredPointKey] : null;
  const pinnedPoint = effectiveShowPoints && pinnedPointKey ? namedPointByKey[pinnedPointKey] : null;

  const isPieceVisible = (pieceKey) => {
    if (pieceKey === 'front') return showFront;
    if (pieceKey === 'side') return showSide;
    if (pieceKey === 'back') return showBack;
    if (pieceKey === 'yoke') return showYoke;
    if (pieceKey === 'collar' || pieceKey === 'collarLayout') return showCollar;
    if (pieceKey === 'topSleeve' || pieceKey === 'underSleeve') return showSleeve;
    if (pieceKey === 'sleeve') return showSleeve;
    return true;
  };

  const selectedPointNameSet = new Set((selectedFreePointNames || []).filter(Boolean));
  const isHardwareVisible = (item) => {
    const piece = item?.piece || 'front';
    if (piece === 'front') return showFront;
    if (piece === 'collar' || piece === 'collarLayout') return showCollar;
    return true;
  };

  const isCurveSegmentVisible = (segmentId = '') => {
    if (segmentId.includes('collar')) return showCollar;
    if (segmentId.includes('sleeve-')) return showSleeve;
    if (segmentId === 'front-side-seam-upper-curve' || segmentId === 'front-side-seam-lower-curve') return showFront;
    if (segmentId === 'back-side-seam-curve') return showBack;
    if (segmentId.includes('side-')) return showSide;
    if (segmentId.includes('front-')) return showFront;
    if (segmentId.includes('back-')) return showBack;
    if (segmentId.includes('yoke')) return showYoke;
    return true;
  };

  const notchStrokeByType = {
    single_t: '#111827',
    single_v: '#0f172a',
    double_t: '#1f2937',
  };

  const activeViewBox = patternData?.viewBox
    ? `${patternData.viewBox.x} ${patternData.viewBox.y} ${patternData.viewBox.width} ${patternData.viewBox.height}`
    : '-10 -50 40 60';
  const garmentCurvePrefix = patternData?.garmentType || 'pattern';

  const getNamedPointColor = (piece) => {
    if (piece === 'front') return '#1e3a8a';
    if (piece === 'side') return '#c2410c';
    if (piece === 'back') return '#dc2626';
    if (piece === 'yoke') return '#0369a1';
    if (piece === 'collar' || piece === 'collarLayout') return '#0f766e';
    if (piece === 'sleeve') return '#9333ea';
    return '#334155';
  };

  const formatInches = (value) => `${Number(value).toFixed(3).replace(/\.0+$/, '').replace(/(\.\d*?)0+$/, '$1')}"`;
  const formatPointTooltipText = (point) => `${point.name} • x ${formatInches(point.x)}, y ${formatInches(-point.y)}`;

  const renderPointTooltip = (point, mode) => {
    if (!point) return null;
    const text = formatPointTooltipText(point);
    const width = Math.max(3.9, text.length * 0.21);
    const x = point.x + 0.3;
    const y = -point.y - 1.1;

    const isPinned = mode === 'pinned';
    return (
      <g
        key={`tooltip-${mode}-${point.piece}-${point.name}`}
        pointerEvents="none"
        data-testid={`canvas-point-${mode}-tooltip-${point.name}`}
      >
        <rect
          x={x}
          y={y}
          rx="0.16"
          ry="0.16"
          width={width}
          height="0.84"
          fill={isPinned ? '#0f172a' : '#1f2937'}
          fillOpacity={0.92}
          stroke={isPinned ? '#38bdf8' : '#cbd5e1'}
          strokeWidth="0.05"
          vectorEffect="non-scaling-stroke"
          data-testid={`canvas-point-${mode}-tooltip-box-${point.name}`}
        />
        <text
          x={x + 0.16}
          y={y + 0.55}
          fontSize="0.34"
          fontFamily="var(--font-mono)"
          fill="#f8fafc"
          fontWeight="600"
          textAnchor="start"
          data-testid={`canvas-point-${mode}-tooltip-text-${point.name}`}
        >
          {text}
        </text>
      </g>
    );
  };

  const seamWalkOverlay = (() => {
    if (!seamWalkMode || patternData?.garmentType !== 'shirt' || !(patternData?.backBasePath || patternData?.backPath)) {
      return null;
    }

    const namedPoints = Array.isArray(patternData?.namedPoints) ? patternData.namedPoints : [];
    const byName = Object.fromEntries(namedPoints.map((point) => [point.name, point]));
    const BHPS = byName.BHPS;
    const BLPS = byName.BLPS;
    const FHPS = byName.FHPS;
    const FLPS = byName.FLPS;
    const FNY = byName.FNY;
    const FAY = byName.FAY;

    if (!BHPS || !BLPS || !FHPS || !FLPS) return null;

    // Named points are stored in internal orientation, convert to SVG coordinates.
    // Requested walk logic:
    // 1) place BLPS on FLPS
    // 2) rotate BACK clockwise around that LPS pivot so BHPS aligns with FLPS→FHPS direction
    const backHps = { x: BHPS.x, y: -BHPS.y };
    const backLps = { x: BLPS.x, y: -BLPS.y };
    const frontHps = { x: FHPS.x, y: -FHPS.y };
    const frontLps = { x: FLPS.x, y: -FLPS.y };

    const backVector = {
      x: backHps.x - backLps.x,
      y: backHps.y - backLps.y,
    };
    const frontVector = {
      x: frontHps.x - frontLps.x,
      y: frontHps.y - frontLps.y,
    };

    const backLen = Math.hypot(backVector.x, backVector.y);
    const frontLen = Math.hypot(frontVector.x, frontVector.y);
    if (backLen < 1e-6 || frontLen < 1e-6) return null;

    const dot = backVector.x * frontVector.x + backVector.y * frontVector.y;
    const cross = backVector.x * frontVector.y - backVector.y * frontVector.x;
    const signedDelta = Math.atan2(cross, dot);

    // Force clockwise rotation only.
    const clockwiseAngle = signedDelta > 0 ? signedDelta - Math.PI * 2 : signedDelta;

    const cosA = Math.cos(clockwiseAngle);
    const sinA = Math.sin(clockwiseAngle);

    const tx = frontLps.x - (cosA * backLps.x - sinA * backLps.y);
    const ty = frontLps.y - (sinA * backLps.x + cosA * backLps.y);

    const transformedBhps = {
      x: cosA * backHps.x - sinA * backHps.y + tx,
      y: sinA * backHps.x + cosA * backHps.y + ty,
    };

    const matrix = {
      a: cosA,
      b: sinA,
      c: -sinA,
      d: cosA,
      e: tx,
      f: ty,
    };

    const mismatch = Math.hypot(transformedBhps.x - frontHps.x, transformedBhps.y - frontHps.y);
    const unitFront = {
      x: frontVector.x / frontLen,
      y: frontVector.y / frontLen,
    };
    const shoulderRayEnd = {
      x: frontLps.x + unitFront.x * Math.max(frontLen, backLen) * 1.08,
      y: frontLps.y + unitFront.y * Math.max(frontLen, backLen) * 1.08,
    };

    const transformedBackPath = (patternData?.garmentType === 'shirt' && patternData?.yokePath)
      ? (patternData?.backPath || patternData?.backBasePath)
      : (patternData?.backBasePath || patternData?.backPath);

    const toSvgPoint = (point) => ({ x: point.x, y: -point.y });
    const transformPoint = (point) => ({
      x: matrix.a * point.x + matrix.c * point.y + matrix.e,
      y: matrix.b * point.x + matrix.d * point.y + matrix.f,
    });

    const yokeGuide = (() => {
      if (!patternData?.yokePath) return null;

      const cb1Raw = byName.CB1;
      const cbyRaw = byName.CBY;
      const blpsRaw = byName.BLPS;
      const bydyRaw = byName.BYDY;
      const bymRaw = byName.BYM;

      const cb1 = cb1Raw ? transformPoint(toSvgPoint(cb1Raw)) : null;
      const cby = cbyRaw ? transformPoint(toSvgPoint(cbyRaw)) : null;
      const blps = blpsRaw ? transformPoint(toSvgPoint(blpsRaw)) : null;
      const bydy = bydyRaw ? transformPoint(toSvgPoint(bydyRaw)) : null;
      const bym = bymRaw ? transformPoint(toSvgPoint(bymRaw)) : null;

      return {
        cb1,
        cby,
        blps,
        bydy,
        bym,
      };
    })();

    const backYokeArmholeCurve = Array.isArray(patternData?.curveSegments)
      ? patternData.curveSegments.find((segment) => segment.id === 'back-yoke-armhole-split-curve')
      : null;

    return {
      matrix,
      transform: `matrix(${matrix.a} ${matrix.b} ${matrix.c} ${matrix.d} ${matrix.e} ${matrix.f})`,
      frontLps,
      frontHps,
      frontNeckCut: FNY ? { x: FNY.x, y: -FNY.y } : null,
      frontArmCut: FAY ? { x: FAY.x, y: -FAY.y } : null,
      transformedBhps,
      shoulderRayEnd,
      mismatch,
      transformedBackPath,
      yokeGuide,
      backYokeArmholeCurve,
    };
  })();

  const jacketSleeveOnBodyOverlay = (() => {
    if (!sleeveOnBodyMode || patternData?.garmentType !== 'jacket' || !hasJacketSleeves) {
      return null;
    }

    const placement = patternData?.sleeveOnBodyPlacement;
    let sourceAnchor = placement?.sourceAnchor ? { x: placement.sourceAnchor.x, y: -placement.sourceAnchor.y } : null;
    let targetAnchor = placement?.targetAnchor ? { x: placement.targetAnchor.x, y: -placement.targetAnchor.y } : null;

    if (!sourceAnchor || !targetAnchor) {
      const namedPoints = Array.isArray(patternData?.namedPoints) ? patternData.namedPoints : [];
      const byName = Object.fromEntries(namedPoints.map((point) => [point.name, point]));
      const usfp = byName.USFP;
      const frontJoinNotch = byName.FRONT_JOIN_NOTCH;
      if (!usfp || !frontJoinNotch) {
        return null;
      }

      sourceAnchor = { x: usfp.x, y: -usfp.y };
      targetAnchor = { x: frontJoinNotch.x, y: -frontJoinNotch.y };
    }

    const translateX = targetAnchor.x - sourceAnchor.x;
    const translateY = targetAnchor.y - sourceAnchor.y;

    return {
      transform: `translate(${translateX} ${translateY})`,
    };
  })();

  return (
    <div className="flex-1 flex flex-col">
      {/* Canvas Area */}
      <div
        ref={canvasRef}
        className="flex-1 relative bg-gray-50/50 overflow-hidden select-none"
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        data-testid="pattern-canvas-interaction-layer"
        style={{ userSelect: 'none', WebkitUserSelect: 'none' }}
      >
        <style>{`
          [data-testid="pattern-canvas-interaction-layer"] text {
            user-select: none;
            -webkit-user-select: none;
          }
        `}</style>
        <div
          className="absolute inset-0 flex items-center justify-center"
          style={{
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`
          }}
        >
          {patternData && hasRenderablePattern && (
            <svg
              ref={svgRef}
              width="95%"
              height="95%"
              viewBox={activeViewBox}
              style={{ fontFamily: 'var(--font-ui)' }}
              className="overflow-visible"
              preserveAspectRatio="xMidYMid meet"
              onMouseDown={clearCurveSelection}
            >
              <g>
                {patternData.garmentType === 'shirt' && showYoke && patternData.yokePath && (
                  <path
                    d={patternData.yokePath}
                    fill="none"
                    stroke="#0369a1"
                    strokeWidth={isYokeSelected ? '0.8' : '0.6'}
                    vectorEffect="non-scaling-stroke"
                    opacity={isYokeSelected || !selectedPiece ? 1 : 0.4}
                    onClick={() => onPieceClick?.('yoke')}
                    className="cursor-pointer hover:stroke-[#0284c7] transition-colors"
                    data-testid="canvas-shirt-yoke-path"
                  />
                )}

                {patternData.garmentType === 'shirt' && showYoke && patternData.yokeDartPath && (
                  <path
                    d={patternData.yokeDartPath}
                    fill="none"
                    stroke="#0e7490"
                    strokeWidth="0.18"
                    vectorEffect="non-scaling-stroke"
                    opacity={isYokeSelected || !selectedPiece ? 0.95 : 0.4}
                    data-testid="canvas-shirt-yoke-dart-path"
                  />
                )}

                {patternData.garmentType === 'shirt' && showCollar && patternData.collarBandPath && (
                  <path
                    d={patternData.collarBandPath}
                    fill="none"
                    stroke="#0f766e"
                    strokeWidth={isCollarSelected ? '0.8' : '0.6'}
                    vectorEffect="non-scaling-stroke"
                    opacity={isCollarSelected || !selectedPiece ? 1 : 0.4}
                    onClick={() => onPieceClick?.('collar')}
                    className="cursor-pointer hover:stroke-[#14b8a6] transition-colors"
                    data-testid="canvas-shirt-collar-band-path"
                  />
                )}

                {patternData.garmentType === 'shirt' && showCollar && patternData.collarLeafPath && (
                  <path
                    d={patternData.collarLeafPath}
                    fill="none"
                    stroke="#0f766e"
                    strokeWidth={isCollarSelected ? '0.75' : '0.55'}
                    vectorEffect="non-scaling-stroke"
                    opacity={isCollarSelected || !selectedPiece ? 0.95 : 0.4}
                    onClick={() => onPieceClick?.('collar')}
                    className="cursor-pointer hover:stroke-[#14b8a6] transition-colors"
                    data-testid="canvas-shirt-collar-leaf-path"
                  />
                )}

                {patternData.garmentType === 'shirt' && showCollar && patternData.collarLeafLayoutPath && (
                  <path
                    d={patternData.collarLeafLayoutPath}
                    fill="none"
                    stroke="#0f766e"
                    strokeWidth={isCollarSelected ? '0.75' : '0.55'}
                    vectorEffect="non-scaling-stroke"
                    opacity={isCollarSelected || !selectedPiece ? 0.95 : 0.4}
                    onClick={() => onPieceClick?.('collar')}
                    className="cursor-pointer hover:stroke-[#14b8a6] transition-colors"
                    data-testid="canvas-shirt-collar-leaf-layout-path"
                  />
                )}

                {patternData.garmentType === 'shirt' && showCollar && patternData.collarBandLayoutPath && (
                  <path
                    d={patternData.collarBandLayoutPath}
                    fill="none"
                    stroke="#0f766e"
                    strokeWidth={isCollarSelected ? '0.75' : '0.55'}
                    vectorEffect="non-scaling-stroke"
                    opacity={isCollarSelected || !selectedPiece ? 0.95 : 0.4}
                    onClick={() => onPieceClick?.('collar')}
                    className="cursor-pointer hover:stroke-[#14b8a6] transition-colors"
                    data-testid="canvas-shirt-collar-band-layout-path"
                  />
                )}

                {patternData.garmentType === 'shirt' && showSleeve && patternData.sleevePath && (
                  <path
                    d={patternData.sleevePath}
                    fill="none"
                    stroke="#9333ea"
                    strokeWidth={isSleeveSelected ? '0.8' : '0.6'}
                    vectorEffect="non-scaling-stroke"
                    opacity={isSleeveSelected || !selectedPiece ? 1 : 0.4}
                    onClick={() => onPieceClick?.('sleeve')}
                    className="cursor-pointer hover:stroke-[#a855f7] transition-colors"
                    data-testid="canvas-shirt-sleeve-path"
                  />
                )}

                {patternData.garmentType === 'shirt' && showSleeve && patternData.sleeveWrappedMirrorPath && (
                  <path
                    d={patternData.sleeveWrappedMirrorPath}
                    fill="none"
                    stroke="#a78bfa"
                    strokeWidth="0.42"
                    vectorEffect="non-scaling-stroke"
                    opacity={isSleeveSelected || !selectedPiece ? 0.92 : 0.35}
                    data-testid="canvas-shirt-sleeve-wrapped-mirror-path"
                  />
                )}

                {patternData.garmentType === 'shirt' && showSleeve && patternData.sleevePlacketPath && (
                  <path
                    d={patternData.sleevePlacketPath}
                    fill="none"
                    stroke="#6d28d9"
                    strokeWidth="0.38"
                    strokeDasharray="0.2 0.12"
                    vectorEffect="non-scaling-stroke"
                    opacity={isSleeveSelected || !selectedPiece ? 0.95 : 0.4}
                    data-testid="canvas-shirt-sleeve-placket-path"
                  />
                )}

                {patternData.garmentType === 'shirt' && showSleeve && patternData.sleeveLengthPath && (
                  <path
                    d={patternData.sleeveLengthPath}
                    fill="none"
                    stroke="#7c3aed"
                    strokeWidth="0.24"
                    strokeDasharray="0.18 0.14"
                    vectorEffect="non-scaling-stroke"
                    opacity={isSleeveSelected || !selectedPiece ? 0.85 : 0.35}
                    data-testid="canvas-shirt-sleeve-length-path"
                  />
                )}

                {hasJacketSleeves && showSleeve && patternData.topSleevePath && (
                  <path
                    d={patternData.topSleevePath}
                    fill="none"
                    stroke="#7c3aed"
                    strokeWidth={isSleeveSelected ? '0.82' : '0.62'}
                    vectorEffect="non-scaling-stroke"
                    opacity={isSleeveSelected || !selectedPiece ? 1 : 0.38}
                    onClick={() => onPieceClick?.('sleeve')}
                    className="cursor-pointer hover:stroke-[#8b5cf6] transition-colors"
                    data-testid="canvas-jacket-top-sleeve-path"
                  />
                )}

                {hasJacketSleeves && showSleeve && patternData.underSleevePath && (
                  <path
                    d={patternData.underSleevePath}
                    fill="none"
                    stroke="#14b8a6"
                    strokeWidth={isSleeveSelected ? '0.82' : '0.62'}
                    vectorEffect="non-scaling-stroke"
                    opacity={isSleeveSelected || !selectedPiece ? 1 : 0.38}
                    onClick={() => onPieceClick?.('sleeve')}
                    className="cursor-pointer hover:stroke-[#0f766e] transition-colors"
                    data-testid="canvas-jacket-under-sleeve-path"
                  />
                )}

                {jacketSleeveOnBodyOverlay && (
                  <g transform={jacketSleeveOnBodyOverlay.transform} pointerEvents="none" data-testid="canvas-jacket-sleeve-on-body-overlay-group">
                    {Array.isArray(patternData.sleeveConstructionLines) && patternData.sleeveConstructionLines.map((line, i) => (
                      <line
                        key={`jacket-sleeve-on-body-guide-${i}`}
                        x1={line.p1.x}
                        y1={-line.p1.y}
                        x2={line.p2.x}
                        y2={-line.p2.y}
                        stroke="#c4b5fd"
                        strokeWidth="0.06"
                        strokeDasharray={line.type === 'crease' ? '0.16 0.14' : '0.22 0.12'}
                        opacity={0.72}
                        vectorEffect="non-scaling-stroke"
                        data-testid={`canvas-jacket-sleeve-on-body-construction-line-${i}`}
                      />
                    ))}
                    {patternData.topSleevePath && (
                      <path
                        d={patternData.topSleevePath}
                        fill="none"
                        stroke="#7c3aed"
                        strokeWidth="0.62"
                        opacity={0.92}
                        vectorEffect="non-scaling-stroke"
                        data-testid="canvas-jacket-sleeve-on-body-top-path"
                      />
                    )}
                    {patternData.underSleevePath && (
                      <path
                        d={patternData.underSleevePath}
                        fill="none"
                        stroke="#14b8a6"
                        strokeWidth="0.62"
                        opacity={0.92}
                        vectorEffect="non-scaling-stroke"
                        data-testid="canvas-jacket-sleeve-on-body-under-path"
                      />
                    )}
                  </g>
                )}

                {!showMeasurements && hasJacketSleeves && showSleeve && patternData.sleeveLengthGuide?.p1 && patternData.sleeveLengthGuide?.p2 && (
                  <g data-testid="canvas-jacket-sleeve-length-guide-group">
                    <line
                      x1={patternData.sleeveLengthGuide.p1.x}
                      y1={patternData.sleeveLengthGuide.p1.y}
                      x2={patternData.sleeveLengthGuide.p2.x}
                      y2={patternData.sleeveLengthGuide.p2.y}
                      stroke="#7c3aed"
                      strokeWidth="0.24"
                      strokeDasharray="0.18 0.14"
                      vectorEffect="non-scaling-stroke"
                      opacity={isSleeveSelected || !selectedPiece ? 0.92 : 0.4}
                      data-testid="canvas-jacket-sleeve-length-path"
                    />
                    {(() => {
                      const x1 = patternData.sleeveLengthGuide.p1.x;
                      const y1 = patternData.sleeveLengthGuide.p1.y;
                      const x2 = patternData.sleeveLengthGuide.p2.x;
                      const y2 = patternData.sleeveLengthGuide.p2.y;
                      const dx = x2 - x1;
                      const dy = y2 - y1;
                      const length = Math.hypot(dx, dy) || 1;
                      const nx = -dy / length;
                      const ny = dx / length;
                      let angle = (Math.atan2(dy, dx) * 180) / Math.PI;
                      if (angle > 90 || angle < -90) angle += 180;
                      const offset = Number(patternData.sleeveLengthGuide.labelOffset || 0.26);
                      return (
                        <text
                          transform={`translate(${((x1 + x2) / 2) + (nx * offset)} ${((y1 + y2) / 2) + (ny * offset)}) rotate(${angle})`}
                          fontSize="0.38"
                          fontFamily="var(--font-mono)"
                          fill="#6d28d9"
                          fontWeight="700"
                          textAnchor="middle"
                          pointerEvents="none"
                          data-testid="canvas-jacket-sleeve-length-text"
                        >
                          {patternData.sleeveLengthGuide.labelText || `${Number(patternData.sleeveLengthGuide.value || 0).toFixed(2)}"`}
                        </text>
                      );
                    })()}
                  </g>
                )}

                {/* Jacket construction guides intentionally minimized to avoid cross-piece visual clutter */}

                {Array.isArray(patternData.frontFishDartLines) && showFront && patternData.frontFishDartLines.map((line) => (
                  <line
                    key={line.id}
                    x1={line.p1.x}
                    y1={-line.p1.y}
                    x2={line.p2.x}
                    y2={-line.p2.y}
                    stroke="#7f1d1d"
                    strokeWidth="0.08"
                    strokeDasharray={line.id.includes('intake') ? '0.16 0.12' : 'none'}
                    opacity={isFrontSelected || !selectedPiece ? 0.95 : 0.35}
                    vectorEffect="non-scaling-stroke"
                    data-testid={`canvas-front-fish-dart-line-${line.id}`}
                  />
                ))}

                {showFront && patternData.garmentType === 'jacket' && patternData.frontDartPath && (
                  <path
                    d={patternData.frontDartPath}
                    fill="none"
                    stroke="#7f1d1d"
                    strokeWidth="0.12"
                    vectorEffect="non-scaling-stroke"
                    opacity={isFrontSelected || !selectedPiece ? 0.95 : 0.35}
                    data-testid="canvas-jacket-front-dart-path"
                  />
                )}

                {showFront && patternData.garmentType === 'jacket' && patternData.lapelPath && (
                  <path
                    d={patternData.lapelPath}
                    fill="none"
                    stroke="#0f172a"
                    strokeWidth="0.3"
                    vectorEffect="non-scaling-stroke"
                    opacity={isFrontSelected || !selectedPiece ? 0.98 : 0.55}
                    data-testid="canvas-jacket-lapel-path"
                  />
                )}

                {showFront && patternData.garmentType === 'jacket' && patternData.rollLineGuide && (
                  <line
                    x1={patternData.rollLineGuide.p1.x}
                    y1={-patternData.rollLineGuide.p1.y}
                    x2={patternData.rollLineGuide.p2.x}
                    y2={-patternData.rollLineGuide.p2.y}
                    stroke="#64748b"
                    strokeWidth="0.14"
                    strokeDasharray="0.22 0.12"
                    opacity={isFrontSelected || !selectedPiece ? 0.95 : 0.45}
                    vectorEffect="non-scaling-stroke"
                    data-testid="canvas-jacket-roll-line-guide"
                  />
                )}

                {showFront && patternData.garmentType === 'jacket' && Array.isArray(patternData.jacketLapelMeasurementLines) && patternData.jacketLapelMeasurementLines.map((line) => (
                  <line
                    key={line.id}
                    x1={line.p1.x}
                    y1={-line.p1.y}
                    x2={line.p2.x}
                    y2={-line.p2.y}
                    stroke={line.stroke || '#0f172a'}
                    strokeWidth={line.strokeWidth || '0.14'}
                    strokeDasharray={line.strokeDasharray || '0.18 0.1'}
                    opacity={isFrontSelected || !selectedPiece ? 0.95 : 0.45}
                    vectorEffect="non-scaling-stroke"
                    data-testid={`canvas-jacket-lapel-measurement-line-${line.id}`}
                  />
                ))}

                {showFront && effectiveShowPoints && patternData.garmentType === 'jacket' && Array.isArray(patternData.jacketPeakGuidePoints) && patternData.jacketPeakGuidePoints.map((point) => (
                  <g key={`peak-guide-${point.name}`} data-testid={`canvas-jacket-peak-guide-point-${point.name}`}>
                    <circle
                      cx={point.x}
                      cy={-point.y}
                      r="0.12"
                      fill="#0f172a"
                      opacity={isFrontSelected || !selectedPiece ? 0.95 : 0.55}
                      pointerEvents="none"
                    />
                    <text
                      x={point.x + 0.14}
                      y={-point.y - 0.12}
                      fontSize="0.5"
                      fill="#0f172a"
                      fontWeight="500"
                      pointerEvents="none"
                      style={{ userSelect: 'none', WebkitUserSelect: 'none' }}
                      data-testid={`canvas-jacket-peak-guide-label-${point.name}`}
                    >
                      {point.name}
                    </text>
                  </g>
                ))}

                {showFront && effectiveShowPoints && peakLapelGuidePoint && (!Array.isArray(patternData.jacketPeakGuidePoints) || patternData.jacketPeakGuidePoints.length === 0) && (
                  <g data-testid={`canvas-jacket-peak-guide-point-${peakLapelGuidePoint.name}`}>
                    <circle
                      cx={peakLapelGuidePoint.x}
                      cy={-peakLapelGuidePoint.y}
                      r="0.12"
                      fill="#0f172a"
                      opacity={isFrontSelected || !selectedPiece ? 0.95 : 0.55}
                      pointerEvents="none"
                    />
                    <text
                      x={peakLapelGuidePoint.x + 0.14}
                      y={-peakLapelGuidePoint.y - 0.12}
                      fontSize="0.5"
                      fill="#0f172a"
                      fontWeight="500"
                      pointerEvents="none"
                      style={{ userSelect: 'none', WebkitUserSelect: 'none' }}
                      data-testid={`canvas-jacket-peak-guide-label-${peakLapelGuidePoint.name}`}
                    >
                      {peakLapelGuidePoint.name}
                    </text>
                  </g>
                )}

                {showFront && patternData.garmentType === 'jacket' && Array.isArray(patternData.lapelConstructionLines) && patternData.lapelConstructionLines
                  .filter((line) => line.id !== 'lapel-shawl-tcol2-rl1'
                    && line.id !== 'lapel-diamond-shawl-tcol2-rl2'
                    && line.id !== 'lapel-diamond-shawl-rl2-rl1'
                    && !hiddenJacketLapelConstructionLineIds.has(line?.id))
                  .map((line) => (
                  <line
                    key={line.id}
                    x1={line.p1.x}
                    y1={-line.p1.y}
                    x2={line.p2.x}
                    y2={-line.p2.y}
                    stroke="#0e7490"
                    strokeWidth="0.08"
                    strokeDasharray="0.14 0.1"
                    opacity={isFrontSelected || !selectedPiece ? 0.95 : 0.35}
                    vectorEffect="non-scaling-stroke"
                    data-testid={`canvas-jacket-lapel-construction-line-${line.id}`}
                  />
                ))}

                {showFront && patternData.garmentType === 'jacket' && Array.isArray(patternData.jacketPocketMarkers) && patternData.jacketPocketMarkers.map((marker) => (
                  <path
                    key={marker.id}
                    d={marker.d}
                    fill="none"
                    stroke={marker.stroke || '#0f172a'}
                    strokeWidth={marker.strokeWidth || '0.18'}
                    vectorEffect="non-scaling-stroke"
                    opacity={isFrontSelected || !selectedPiece ? 0.98 : 0.45}
                    strokeDasharray={marker.id.includes('side-pocket') ? 'none' : undefined}
                    data-testid={`canvas-jacket-pocket-marker-${marker.id}`}
                  />
                ))}

                {showFront && patternData.garmentType === 'jacket' && Array.isArray(patternData.jacketCollarPaths) && patternData.jacketCollarPaths.map((collarPath) => (
                  <path
                    key={collarPath.id}
                    d={collarPath.d}
                    fill="none"
                    stroke={collarPath.stroke || '#0f172a'}
                    strokeWidth={collarPath.strokeWidth || '0.18'}
                    strokeDasharray={collarPath.strokeDasharray || undefined}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    vectorEffect="non-scaling-stroke"
                    opacity={isFrontSelected || !selectedPiece ? 0.98 : 0.45}
                    pointerEvents={collarPath.id === 'folded-top-collar-preview' ? 'none' : 'none'}
                    data-testid={`canvas-jacket-collar-path-${collarPath.id}`}
                  />
                ))}

                {showFront && patternData.garmentType === 'jacket' && Array.isArray(patternData.jacketShawlFoldedPreviewPaths) && patternData.jacketShawlFoldedPreviewPaths.map((previewPath) => (
                  <path
                    key={previewPath.id}
                    d={previewPath.d}
                    fill="none"
                    stroke={previewPath.stroke || '#0f766e'}
                    strokeWidth={previewPath.strokeWidth || '0.18'}
                    strokeDasharray={previewPath.strokeDasharray || '0.18 0.1'}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    vectorEffect="non-scaling-stroke"
                    opacity={isFrontSelected || !selectedPiece ? 0.92 : 0.4}
                    pointerEvents="none"
                    data-testid={`canvas-jacket-shawl-folded-preview-${previewPath.id}`}
                  />
                ))}

                {patternData.garmentType === 'shirt' && showSleeve && Array.isArray(patternData.sleeveConstructionLines) && patternData.sleeveConstructionLines.map((line, i) => (
                  <line
                    key={`sc-${i}`}
                    x1={line.p1.x}
                    y1={-line.p1.y}
                    x2={line.p2.x}
                    y2={-line.p2.y}
                    stroke="#c4b5fd"
                    strokeWidth="0.06"
                    strokeDasharray="0.22 0.12"
                    opacity={isSleeveSelected || !selectedPiece ? 0.95 : 0.35}
                    data-testid={`canvas-shirt-sleeve-construction-line-${i}`}
                  />
                ))}

                {hasJacketSleeves && showSleeve && Array.isArray(patternData.sleeveConstructionLines) && patternData.sleeveConstructionLines.map((line, i) => (
                  <line
                    key={`jacket-sleeve-guide-${i}`}
                    x1={line.p1.x}
                    y1={-line.p1.y}
                    x2={line.p2.x}
                    y2={-line.p2.y}
                    stroke="#c4b5fd"
                    strokeWidth="0.06"
                    strokeDasharray={line.type === 'crease' ? '0.16 0.14' : '0.22 0.12'}
                    opacity={isSleeveSelected || !selectedPiece ? 0.9 : 0.32}
                    data-testid={`canvas-jacket-sleeve-construction-line-${i}`}
                  />
                ))}

                {Array.isArray(patternData.notchMarks) && patternData.notchMarks
                  .filter((mark) => isPieceVisible(mark.piece))
                  .map((mark) => (
                    <g key={mark.id} data-testid={`canvas-notch-${mark.id}`}>
                      {(mark.paths || []).map((notchPath, notchIndex) => (
                        <path
                          key={`${mark.id}-${notchIndex}`}
                          d={notchPath}
                          fill="none"
                          stroke={notchStrokeByType[mark.type] || '#111827'}
                          strokeWidth="0.24"
                          vectorEffect="non-scaling-stroke"
                          opacity={0.95}
                        />
                      ))}
                    </g>
                  ))}

                {/* Crease Lines */}
                {patternData.fCreasePath && showFront && (
                  <path
                    d={patternData.fCreasePath}
                    fill="none"
                    stroke="#94a3b8"
                    strokeWidth="0.28"
                    strokeDasharray="0.3 0.1"
                    vectorEffect="non-scaling-stroke"
                    opacity={isFrontSelected || !selectedPiece ? 1 : 0.3}
                  />
                )}
                {patternData.bCreasePath && showBack && (
                  <path
                    d={patternData.bCreasePath}
                    fill="none"
                    stroke="#f87171"
                    strokeWidth="0.28"
                    strokeDasharray="0.3 0.1"
                    vectorEffect="non-scaling-stroke"
                    opacity={isBackSelected || !selectedPiece ? 1 : 0.3}
                  />
                )}

                {showFront && patternData.pleatLines && patternData.pleatLines.map((line, i) => (
                  <line
                    key={`pleat-${i}`}
                    x1={line.x1}
                    y1={-line.y1}
                    x2={line.x2}
                    y2={-line.y2}
                    stroke="#f59e0b"
                    strokeWidth="0.28"
                    strokeDasharray="0.35 0.2"
                    vectorEffect="non-scaling-stroke"
                    opacity={isFrontSelected || !selectedPiece ? 0.95 : 0.3}
                    data-testid={`front-pleat-line-${i}`}
                  />
                ))}

                {showBack && patternData.garmentType === 'shirt' && Array.isArray(patternData.backPleatLines) && patternData.backPleatLines.map((line, i) => (
                  <line
                    key={`shirt-back-pleat-${line.id || i}`}
                    x1={line.x1}
                    y1={-line.y1}
                    x2={line.x2}
                    y2={-line.y2}
                    stroke={line.type === 'fold' ? '#020617' : '#334155'}
                    strokeWidth={line.type === 'fold' ? '0.22' : '0.22'}
                    strokeDasharray={line.type === 'underlay' ? '0.2 0.1' : undefined}
                    vectorEffect="non-scaling-stroke"
                    opacity={0.98}
                    data-testid={`shirt-back-pleat-line-${i}`}
                  />
                ))}

                {/* Version Comparison Overlays */}
                {comparisonPatterns.map((overlay) => (
                  <g key={`overlay-${overlay.id || overlay.pattern_number}`}>
                    {showFront && overlay.frontPath && (
                      <path
                        d={overlay.frontPath}
                        fill="none"
                        stroke={overlay.color}
                        strokeWidth="0.4"
                        strokeDasharray="0.25 0.15"
                        vectorEffect="non-scaling-stroke"
                        opacity={0.9}
                        pointerEvents="none"
                        data-testid={`comparison-front-${overlay.id}`}
                      />
                    )}
                    {showSide && overlay.sidePath && (
                      <path
                        d={overlay.sidePath}
                        fill="none"
                        stroke={overlay.color}
                        strokeWidth="0.4"
                        strokeDasharray="0.25 0.15"
                        vectorEffect="non-scaling-stroke"
                        opacity={0.9}
                        pointerEvents="none"
                        data-testid={`comparison-side-${overlay.id}`}
                      />
                    )}
                    {showBack && overlay.backPath && (
                      <path
                        d={overlay.backPath}
                        fill="none"
                        stroke={overlay.color}
                        strokeWidth="0.4"
                        strokeDasharray="0.25 0.15"
                        vectorEffect="non-scaling-stroke"
                        opacity={0.9}
                        pointerEvents="none"
                        data-testid={`comparison-back-${overlay.id}`}
                      />
                    )}
                    {showSleeve && overlay.sleevePath && (
                      <path
                        d={overlay.sleevePath}
                        fill="none"
                        stroke={overlay.color}
                        strokeWidth="0.4"
                        strokeDasharray="0.25 0.15"
                        vectorEffect="non-scaling-stroke"
                        opacity={0.9}
                        pointerEvents="none"
                        data-testid={`comparison-sleeve-${overlay.id}`}
                      />
                    )}
                  </g>
                ))}

                {/* Front Pattern */}
                {showFront && (
                  <path
                    d={patternData.frontPath}
                    fill="none"
                    stroke="#1e3a8a"
                    strokeWidth={isFrontSelected ? "0.8" : "0.6"}
                    vectorEffect="non-scaling-stroke"
                    opacity={isFrontSelected || !selectedPiece ? 1 : 0.4}
                    onClick={() => onPieceClick?.('front')}
                    className="cursor-pointer hover:stroke-[#2563eb] transition-colors"
                    data-testid="canvas-front-pattern-path"
                  />
                )}

                {showSide && patternData.sidePath && (
                  <path
                    d={patternData.sidePath}
                    fill="none"
                    stroke="#c2410c"
                    strokeWidth={isSideSelected ? '0.8' : '0.6'}
                    vectorEffect="non-scaling-stroke"
                    opacity={isSideSelected || !selectedPiece ? 1 : 0.4}
                    onClick={() => onPieceClick?.('side')}
                    className="cursor-pointer hover:stroke-[#ea580c] transition-colors"
                    data-testid="canvas-side-pattern-path"
                  />
                )}

                {/* Back Pattern */}
                {showBack && patternData.backPath && (
                  <path
                    d={patternData.backPath}
                    fill="none"
                    stroke="#dc2626"
                    strokeWidth={isBackSelected ? "0.8" : "0.6"}
                    vectorEffect="non-scaling-stroke"
                    opacity={isBackSelected || !selectedPiece ? 1 : 0.4}
                    onClick={() => onPieceClick?.('back')}
                    className="cursor-pointer hover:stroke-[#ef4444] transition-colors"
                    data-testid="canvas-back-pattern-path"
                  />
                )}

                {seamWalkOverlay && (
                  <g data-testid="shirt-seam-walk-overlay-group" pointerEvents="none">
                    {seamWalkOverlay.frontNeckCut && (
                      <line
                        x1={seamWalkOverlay.frontHps.x}
                        y1={seamWalkOverlay.frontHps.y}
                        x2={seamWalkOverlay.frontNeckCut.x}
                        y2={seamWalkOverlay.frontNeckCut.y}
                        stroke="#1d4ed8"
                        strokeWidth="0.12"
                        strokeDasharray="0.14 0.1"
                        opacity={0.9}
                        vectorEffect="non-scaling-stroke"
                        data-testid="shirt-seam-walk-front-hps-to-fny-soft-line"
                      />
                    )}

                    {seamWalkOverlay.frontArmCut && (
                      <line
                        x1={seamWalkOverlay.frontLps.x}
                        y1={seamWalkOverlay.frontLps.y}
                        x2={seamWalkOverlay.frontArmCut.x}
                        y2={seamWalkOverlay.frontArmCut.y}
                        stroke="#0891b2"
                        strokeWidth="0.12"
                        strokeDasharray="0.14 0.1"
                        opacity={0.9}
                        vectorEffect="non-scaling-stroke"
                        data-testid="shirt-seam-walk-front-lps-to-fay-soft-line"
                      />
                    )}

                    <line
                      x1={seamWalkOverlay.frontLps.x}
                      y1={seamWalkOverlay.frontLps.y}
                      x2={seamWalkOverlay.shoulderRayEnd.x}
                      y2={seamWalkOverlay.shoulderRayEnd.y}
                      stroke="#0f766e"
                      strokeWidth="0.12"
                      strokeDasharray="0.24 0.16"
                      opacity={0.8}
                      vectorEffect="non-scaling-stroke"
                      data-testid="shirt-seam-walk-front-shoulder-ray"
                    />

                    <path
                      d={seamWalkOverlay.transformedBackPath}
                      transform={seamWalkOverlay.transform}
                      fill="none"
                      stroke="#0f766e"
                      strokeWidth="0.18"
                      opacity={0.9}
                      vectorEffect="non-scaling-stroke"
                      data-testid="shirt-seam-walk-transformed-back-path"
                    />

                    {seamWalkOverlay.yokeGuide?.cb1 && seamWalkOverlay.yokeGuide?.cby && (
                      <line
                        x1={seamWalkOverlay.yokeGuide.cb1.x}
                        y1={seamWalkOverlay.yokeGuide.cb1.y}
                        x2={seamWalkOverlay.yokeGuide.cby.x}
                        y2={seamWalkOverlay.yokeGuide.cby.y}
                        stroke="#0f766e"
                        strokeWidth="0.14"
                        strokeDasharray="0.18 0.12"
                        opacity={0.95}
                        vectorEffect="non-scaling-stroke"
                        data-testid="shirt-seam-walk-yoke-segment-cb1-cby"
                      />
                    )}

                    {seamWalkOverlay.backYokeArmholeCurve?.d ? (
                      <path
                        d={seamWalkOverlay.backYokeArmholeCurve.d}
                        transform={seamWalkOverlay.transform}
                        fill="none"
                        stroke="#0f766e"
                        strokeWidth="0.16"
                        strokeDasharray="0.18 0.12"
                        opacity={0.95}
                        vectorEffect="non-scaling-stroke"
                        data-testid="shirt-seam-walk-yoke-segment-blps-bydy"
                      />
                    ) : seamWalkOverlay.yokeGuide?.blps && seamWalkOverlay.yokeGuide?.bydy && (
                      <line
                        x1={seamWalkOverlay.yokeGuide.blps.x}
                        y1={seamWalkOverlay.yokeGuide.blps.y}
                        x2={seamWalkOverlay.yokeGuide.bydy.x}
                        y2={seamWalkOverlay.yokeGuide.bydy.y}
                        stroke="#0f766e"
                        strokeWidth="0.14"
                        strokeDasharray="0.18 0.12"
                        opacity={0.95}
                        vectorEffect="non-scaling-stroke"
                        data-testid="shirt-seam-walk-yoke-segment-blps-bydy"
                      />
                    )}

                    {seamWalkOverlay.yokeGuide?.cby && seamWalkOverlay.yokeGuide?.bydy && (
                      <polyline
                        points={
                          seamWalkOverlay.yokeGuide?.bym
                            ? `${seamWalkOverlay.yokeGuide.cby.x},${seamWalkOverlay.yokeGuide.cby.y} ${seamWalkOverlay.yokeGuide.bym.x},${seamWalkOverlay.yokeGuide.bym.y} ${seamWalkOverlay.yokeGuide.bydy.x},${seamWalkOverlay.yokeGuide.bydy.y}`
                            : `${seamWalkOverlay.yokeGuide.cby.x},${seamWalkOverlay.yokeGuide.cby.y} ${seamWalkOverlay.yokeGuide.bydy.x},${seamWalkOverlay.yokeGuide.bydy.y}`
                        }
                        fill="none"
                        stroke="#0f766e"
                        strokeWidth="0.14"
                        strokeDasharray="0.18 0.12"
                        opacity={0.95}
                        vectorEffect="non-scaling-stroke"
                        data-testid="shirt-seam-walk-yoke-segment-cby-bym-bydy"
                      />
                    )}

                    {Array.isArray(patternData?.curveSegments) && patternData.curveSegments
                      .filter((segment) => (
                        segment.id === 'back-neck-curve'
                        || segment.id === 'back-armhole-part1-curve'
                        || segment.id === 'back-armhole-part2-curve'
                      ))
                      .map((segment) => (
                        <path
                          key={`seam-walk-${segment.id}`}
                          d={segment.d}
                          transform={seamWalkOverlay.transform}
                          fill="none"
                          stroke="#14b8a6"
                          strokeWidth="0.2"
                          opacity={1}
                          vectorEffect="non-scaling-stroke"
                          data-testid={`shirt-seam-walk-${segment.id}`}
                        />
                      ))}

                    {seamWalkOverlay.mismatch > 0.02 && (
                      <line
                        x1={seamWalkOverlay.frontHps.x}
                        y1={seamWalkOverlay.frontHps.y}
                        x2={seamWalkOverlay.transformedBhps.x}
                        y2={seamWalkOverlay.transformedBhps.y}
                        stroke="#f59e0b"
                        strokeWidth="0.08"
                        strokeDasharray="0.12 0.1"
                        opacity={0.85}
                        vectorEffect="non-scaling-stroke"
                        data-testid="shirt-seam-walk-hps-mismatch-line"
                      />
                    )}
                  </g>
                )}

                {/* Dart */}
                {showFront && patternData.bustDartPath && (
                  <path
                    d={patternData.bustDartPath}
                    fill="none"
                    stroke="#be123c"
                    strokeWidth={0.22}
                    strokeDasharray="0.35 0.25"
                    vectorEffect="non-scaling-stroke"
                  />
                )}
                {showFront && patternData.frontFishDartPath && (
                  <path
                    d={patternData.frontFishDartPath}
                    fill="none"
                    stroke="#0f766e"
                    strokeWidth={0.2}
                    vectorEffect="non-scaling-stroke"
                  />
                )}
                {showBack && patternData.dartPath && (
                  <path
                    d={patternData.dartPath}
                    fill="none"
                    stroke="#dc2626"
                    strokeWidth="0.2"
                    vectorEffect="non-scaling-stroke"
                    opacity={isBackSelected || !selectedPiece ? 1 : 0.3}
                  />
                )}

                {patternData.garmentType === 'shirt' && showFront && patternData.buttonExtensionPath && (
                  <path
                    d={patternData.buttonExtensionPath}
                    fill="none"
                    stroke="#0f766e"
                    strokeWidth="0.14"
                    strokeDasharray="0.18 0.12"
                    vectorEffect="non-scaling-stroke"
                    data-testid="canvas-shirt-button-extension-path"
                  />
                )}

                {patternData.garmentType === 'shirt' && showFront && patternData.placketFoldPath && (
                  <path
                    d={patternData.placketFoldPath}
                    fill="none"
                    stroke="#0891b2"
                    strokeWidth="0.14"
                    strokeDasharray="0.18 0.12"
                    vectorEffect="non-scaling-stroke"
                    data-testid="canvas-shirt-placket-fold-path"
                  />
                )}

                {/* Front Measurements */}
                {showMeasurements && showFront && patternData.frontMeasurements &&
                  patternData.frontMeasurements.map((m, i) => (
                    <g key={`f-${i}`}>
                      {m.referenceLine && (
                        <line
                          x1={m.p1.x}
                          y1={-m.p1.y}
                          x2={m.p2.x}
                          y2={-m.p2.y}
                          stroke="#1e40af"
                          strokeWidth="0.15"
                          opacity={0.75}
                          vectorEffect="non-scaling-stroke"
                        />
                      )}
                      <text
                        x={(m.p1.x + m.p2.x) / 2}
                        y={-(m.p1.y + m.p2.y) / 2 - (m.offset || 0.35)}
                        fontSize="0.5"
                        fontFamily="var(--font-mono)"
                        fill="#1e40af"
                        fontWeight="500"
                        textAnchor="middle"
                        pointerEvents="none"
                      >
                        {m.showLabel === false ? `${m.value}"` : `${m.label}: ${m.value}"`}
                      </text>
                    </g>
                  ))}

                {/* Back Measurements */}
                {showMeasurements && showBack && patternData.backMeasurements &&
                  patternData.backMeasurements.map((m, i) => (
                    <g key={`b-${i}`}>
                      {m.referenceLine && (
                        <line
                          x1={m.p1.x}
                          y1={-m.p1.y}
                          x2={m.p2.x}
                          y2={-m.p2.y}
                          stroke="#dc2626"
                          strokeWidth="0.15"
                          opacity={0.75}
                          vectorEffect="non-scaling-stroke"
                        />
                      )}
                      <text
                        x={(m.p1.x + m.p2.x) / 2}
                        y={-(m.p1.y + m.p2.y) / 2 - (m.offset || 0.35)}
                        fontSize="0.5"
                        fontFamily="var(--font-mono)"
                        fill="#dc2626"
                        fontWeight="500"
                        textAnchor="middle"
                        pointerEvents="none"
                      >
                        {m.showLabel === false ? `${m.value}"` : `${m.label}: ${m.value}"`}
                      </text>
                    </g>
                  ))}

                {/* Side Measurements */}
                {showMeasurements && showSide && patternData.sideMeasurements &&
                  patternData.sideMeasurements.map((m, i) => (
                    <g key={`s-${i}`}>
                      {m.referenceLine && (
                        <line
                          x1={m.p1.x}
                          y1={-m.p1.y}
                          x2={m.p2.x}
                          y2={-m.p2.y}
                          stroke="#c2410c"
                          strokeWidth="0.15"
                          opacity={0.75}
                          vectorEffect="non-scaling-stroke"
                        />
                      )}
                      <text
                        x={(m.p1.x + m.p2.x) / 2}
                        y={-(m.p1.y + m.p2.y) / 2 - (m.offset || 0.35)}
                        fontSize="0.5"
                        fontFamily="var(--font-mono)"
                        fill="#c2410c"
                        fontWeight="500"
                        textAnchor="middle"
                        pointerEvents="none"
                      >
                        {m.showLabel === false ? `${m.value}"` : `${m.label}: ${m.value}"`}
                      </text>
                    </g>
                  ))}

                {showMeasurements && showSleeve && patternData.garmentType === 'shirt' && namedPointByName.BiC1 && namedPointByName.BiC2 && (
                  <g data-testid="shirt-sleeve-bicep-measurement-group">
                    <line
                      x1={namedPointByName.BiC1.x}
                      y1={-namedPointByName.BiC1.y}
                      x2={namedPointByName.BiC2.x}
                      y2={-namedPointByName.BiC2.y}
                      stroke="#6d28d9"
                      strokeWidth="0.15"
                      strokeDasharray="0.25 0.14"
                      opacity={0.85}
                      vectorEffect="non-scaling-stroke"
                      data-testid="shirt-sleeve-bicep-measurement-line"
                    />
                    <text
                      x={(namedPointByName.BiC1.x + namedPointByName.BiC2.x) / 2}
                      y={-((namedPointByName.BiC1.y + namedPointByName.BiC2.y) / 2) - 0.42}
                      fontSize="0.48"
                      fontFamily="var(--font-mono)"
                      fill="#6d28d9"
                      fontWeight="500"
                      textAnchor="middle"
                      pointerEvents="none"
                      data-testid="shirt-sleeve-bicep-measurement-text"
                    >
                      Bicep: {Number(patternData?.matchingSummary?.sleeveBicepUnwrappedDrawn || 0).toFixed(2)}"
                    </text>
                  </g>
                )}

                {showMeasurements && showSleeve && patternData.garmentType === 'shirt' && patternData?.matchingSummary?.sleeveStyle !== 'short' && namedPointByName.ELC1 && namedPointByName.ELC2 && (
                  <g data-testid="shirt-sleeve-elbow-measurement-group">
                    <line
                      x1={namedPointByName.ELC1.x}
                      y1={-namedPointByName.ELC1.y}
                      x2={namedPointByName.ELC2.x}
                      y2={-namedPointByName.ELC2.y}
                      stroke="#7c3aed"
                      strokeWidth="0.15"
                      strokeDasharray="0.25 0.14"
                      opacity={0.85}
                      vectorEffect="non-scaling-stroke"
                      data-testid="shirt-sleeve-elbow-measurement-line"
                    />
                    <text
                      x={(namedPointByName.ELC1.x + namedPointByName.ELC2.x) / 2}
                      y={-((namedPointByName.ELC1.y + namedPointByName.ELC2.y) / 2) - 0.42}
                      fontSize="0.48"
                      fontFamily="var(--font-mono)"
                      fill="#7c3aed"
                      fontWeight="500"
                      textAnchor="middle"
                      pointerEvents="none"
                      data-testid="shirt-sleeve-elbow-measurement-text"
                    >
                      Elbow: {Number(patternData?.matchingSummary?.sleeveElbowUnwrappedDrawn || 0).toFixed(2)}"
                    </text>
                  </g>
                )}

                {showMeasurements && showSleeve && patternData.garmentType === 'shirt' && namedPointByName.HC1 && namedPointByName.HC2 && (
                  <g data-testid="shirt-sleeve-hem-measurement-group">
                    <line
                      x1={namedPointByName.HC1.x}
                      y1={-namedPointByName.HC1.y}
                      x2={namedPointByName.HC2.x}
                      y2={-namedPointByName.HC2.y}
                      stroke="#6d28d9"
                      strokeWidth="0.15"
                      strokeDasharray="0.25 0.14"
                      opacity={0.85}
                      vectorEffect="non-scaling-stroke"
                      data-testid="shirt-sleeve-hem-measurement-line"
                    />
                    <text
                      x={(namedPointByName.HC1.x + namedPointByName.HC2.x) / 2}
                      y={-((namedPointByName.HC1.y + namedPointByName.HC2.y) / 2) - 0.42}
                      fontSize="0.48"
                      fontFamily="var(--font-mono)"
                      fill="#6d28d9"
                      fontWeight="500"
                      textAnchor="middle"
                      pointerEvents="none"
                      data-testid="shirt-sleeve-hem-measurement-text"
                    >
                      Hem: {Number(patternData?.matchingSummary?.sleeveHemUnwrappedDrawn || 0).toFixed(2)}"
                    </text>
                  </g>
                )}

                {showMeasurements && showSleeve && patternData.garmentType === 'shirt' && namedPointByName.SL1 && patternData?.matchingSummary?.sleeveCapPathLength && (
                  <text
                    x={namedPointByName.SL1.x}
                    y={-namedPointByName.SL1.y - 0.78}
                    fontSize="0.5"
                    fontFamily="var(--font-mono)"
                    fill="#4c1d95"
                    fontWeight="500"
                    textAnchor="middle"
                    pointerEvents="none"
                    data-testid="shirt-total-sleeve-armhole-canvas-text"
                  >
                    Sleeve Cap: {Number(patternData.matchingSummary.sleeveCapPathLength).toFixed(2)}" / Target: {Number(patternData.matchingSummary.sleeveCapTarget || 0).toFixed(2)}"
                  </text>
                )}

                {showMeasurements && hasJacketSleeves && showSleeve && namedPointByName.BiB && namedPointByName.BiF && (
                  <g data-testid="jacket-sleeve-bicep-measurement-group">
                    <line
                      x1={namedPointByName.BiB.x}
                      y1={-namedPointByName.BiB.y}
                      x2={namedPointByName.BiF.x}
                      y2={-namedPointByName.BiF.y}
                      stroke="#7c3aed"
                      strokeWidth="0.15"
                      strokeDasharray="0.25 0.14"
                      opacity={0.88}
                      vectorEffect="non-scaling-stroke"
                      data-testid="jacket-sleeve-bicep-measurement-line"
                    />
                    <text
                      x={(namedPointByName.BiB.x + namedPointByName.BiF.x) / 2}
                      y={-((namedPointByName.BiB.y + namedPointByName.BiF.y) / 2) - 0.42}
                      fontSize="0.48"
                      fontFamily="var(--font-mono)"
                      fill="#7c3aed"
                      fontWeight="600"
                      textAnchor="middle"
                      pointerEvents="none"
                      data-testid="jacket-sleeve-bicep-measurement-text"
                    >
                      Bicep: {Number(patternData?.matchingSummary?.jacketSleeveBicepTotalDrawn || 0).toFixed(2)}&quot;
                    </text>
                  </g>
                )}

                {showMeasurements && hasJacketSleeves && showSleeve && namedPointByName.ElB && namedPointByName.ElF && (
                  <g data-testid="jacket-sleeve-elbow-measurement-group">
                    <line
                      x1={namedPointByName.ElB.x}
                      y1={-namedPointByName.ElB.y}
                      x2={namedPointByName.ElF.x}
                      y2={-namedPointByName.ElF.y}
                      stroke="#0f766e"
                      strokeWidth="0.15"
                      strokeDasharray="0.25 0.14"
                      opacity={0.88}
                      vectorEffect="non-scaling-stroke"
                      data-testid="jacket-sleeve-elbow-measurement-line"
                    />
                    <text
                      x={(namedPointByName.ElB.x + namedPointByName.ElF.x) / 2}
                      y={-((namedPointByName.ElB.y + namedPointByName.ElF.y) / 2) - 0.42}
                      fontSize="0.48"
                      fontFamily="var(--font-mono)"
                      fill="#0f766e"
                      fontWeight="600"
                      textAnchor="middle"
                      pointerEvents="none"
                      data-testid="jacket-sleeve-elbow-measurement-text"
                    >
                      Elbow: {Number(patternData?.matchingSummary?.jacketSleeveElbowTotalDrawn || 0).toFixed(2)}&quot;
                    </text>
                  </g>
                )}

                {showMeasurements && hasJacketSleeves && showSleeve && namedPointByName.HB && namedPointByName.HF && (
                  <g data-testid="jacket-sleeve-hem-measurement-group">
                    <line
                      x1={namedPointByName.HB.x}
                      y1={-namedPointByName.HB.y}
                      x2={namedPointByName.HF.x}
                      y2={-namedPointByName.HF.y}
                      stroke="#10b981"
                      strokeWidth="0.15"
                      strokeDasharray="0.25 0.14"
                      opacity={0.88}
                      vectorEffect="non-scaling-stroke"
                      data-testid="jacket-sleeve-hem-measurement-line"
                    />
                    <text
                      x={(namedPointByName.HB.x + namedPointByName.HF.x) / 2}
                      y={-((namedPointByName.HB.y + namedPointByName.HF.y) / 2) - 0.42}
                      fontSize="0.48"
                      fontFamily="var(--font-mono)"
                      fill="#10b981"
                      fontWeight="600"
                      textAnchor="middle"
                      pointerEvents="none"
                      data-testid="jacket-sleeve-hem-measurement-text"
                    >
                      Hem: {Number(patternData?.matchingSummary?.jacketSleeveHemTotalDrawn || 0).toFixed(2)}&quot;
                    </text>
                  </g>
                )}

                {showMeasurements && hasJacketSleeves && showSleeve && namedPointByName.SLT && namedPointByName.HB && namedPointByName.TSHF && (
                  <g data-testid="jacket-sleeve-length-measurement-group">
                    {(() => {
                      const endX = (namedPointByName.HB.x + namedPointByName.TSHF.x) / 2;
                      const endY = -((namedPointByName.HB.y + namedPointByName.TSHF.y) / 2);
                      const x1 = namedPointByName.SLT.x;
                      const y1 = -namedPointByName.SLT.y;
                      const dx = endX - x1;
                      const dy = endY - y1;
                      const length = Math.hypot(dx, dy) || 1;
                      const nx = -dy / length;
                      const ny = dx / length;
                      let angle = (Math.atan2(dy, dx) * 180) / Math.PI;
                      if (angle > 90 || angle < -90) angle += 180;
                      return (
                        <>
                          <line
                            x1={x1}
                            y1={y1}
                            x2={endX}
                            y2={endY}
                            stroke="#6d28d9"
                            strokeWidth="0.16"
                            strokeDasharray="0.22 0.14"
                            opacity={0.92}
                            vectorEffect="non-scaling-stroke"
                            data-testid="canvas-jacket-sleeve-length-path"
                          />
                          <text
                            transform={`translate(${((x1 + endX) / 2) + (nx * 0.28)} ${((y1 + endY) / 2) + (ny * 0.28)}) rotate(${angle})`}
                            fontSize="0.4"
                            fontFamily="var(--font-mono)"
                            fill="#6d28d9"
                            fontWeight="700"
                            textAnchor="middle"
                            pointerEvents="none"
                            data-testid="canvas-jacket-sleeve-length-text"
                          >
                            Sleeve: {Number(patternData?.matchingSummary?.jacketSleeveLengthMidpointDrawn || 0).toFixed(2)}&quot;
                          </text>
                        </>
                      );
                    })()}
                  </g>
                )}

                {showMeasurements && hasJacketSleeves && showSleeve && namedPointByName.SLT && patternData?.matchingSummary?.sleeveCapPathLength && (
                  <text
                    x={namedPointByName.SLT.x}
                    y={-namedPointByName.SLT.y - 0.82}
                    fontSize="0.5"
                    fontFamily="var(--font-mono)"
                    fill="#4c1d95"
                    fontWeight="600"
                    textAnchor="middle"
                    pointerEvents="none"
                    data-testid="jacket-sleeve-cap-summary-canvas-text"
                  >
                    Cap: {Number(patternData.matchingSummary.sleeveCapPathLength).toFixed(2)}&quot; / Target: {Number(patternData.matchingSummary.sleeveCapTarget || 0).toFixed(2)}&quot;
                  </text>
                )}

                {showMeasurements && hasJacketSleeves && showSleeve && [
                  { key: 'bp-shoulder-notch', start: 'BP', end: 'SLEEVE_SHOULDER_NOTCH', value: Number(patternData?.matchingSummary?.sleeveTopBackCrownLength || 0), stroke: '#7c3aed', offset: -0.22 },
                  { key: 'shoulder-notch-tsfp', start: 'SLEEVE_SHOULDER_NOTCH', end: 'TSFP', value: Number(patternData?.matchingSummary?.sleeveTopFrontCrownLength || 0), stroke: '#7c3aed', offset: 0.22 },
                  { key: 'bp-usfp', start: 'BP', end: 'USFP', value: Number(patternData?.matchingSummary?.sleeveUnderJoinLength || 0), stroke: '#0f766e', offset: -0.14 },
                ].filter((segment) => namedPointByName[segment.start] && namedPointByName[segment.end]).map((segment) => {
                  const start = namedPointByName[segment.start];
                  const end = namedPointByName[segment.end];
                  const x1 = start.x;
                  const y1 = -start.y;
                  const x2 = end.x;
                  const y2 = -end.y;
                  const dx = x2 - x1;
                  const dy = y2 - y1;
                  const length = Math.hypot(dx, dy) || 1;
                  const nx = -dy / length;
                  const ny = dx / length;
                  let angle = (Math.atan2(dy, dx) * 180) / Math.PI;
                  if (angle > 90 || angle < -90) angle += 180;

                  return (
                    <text
                      key={segment.key}
                      transform={`translate(${((x1 + x2) / 2) + (nx * segment.offset)} ${((y1 + y2) / 2) + (ny * segment.offset)}) rotate(${angle})`}
                      fontSize="0.38"
                      fontFamily="var(--font-mono)"
                      fill={segment.stroke}
                      fontWeight="600"
                      textAnchor="middle"
                      pointerEvents="none"
                      data-testid={`jacket-sleeve-cap-segment-${segment.key}-text`}
                    >
                      {segment.value.toFixed(2)}&quot;
                    </text>
                  );
                })}

                {showMeasurements && hasJacketSleeves && showSleeve && [
                  { key: 'back-hb-elb', start: 'HB', end: 'ElB', value: Number(patternData?.matchingSummary?.jacketSleeveBackSeam_hbToElb || 0), stroke: '#0f766e', offset: -0.42 },
                  { key: 'back-elb-bp', start: 'ElB', end: 'BP', value: Number(patternData?.matchingSummary?.jacketSleeveBackSeam_elbToBp || 0), stroke: '#0f766e', offset: -0.38 },
                  { key: 'us-usfp-useif', start: 'USFP', end: 'USElF', value: Number(patternData?.matchingSummary?.jacketSleeveUsFrontSeam_usfpToUsElf || 0), stroke: '#0f766e', offset: 0.34 },
                  { key: 'us-useif-ushf', start: 'USElF', end: 'USHF', value: Number(patternData?.matchingSummary?.jacketSleeveUsFrontSeam_usElfToUsHf || 0), stroke: '#0f766e', offset: 0.34 },
                  { key: 'ts-tsfp-tself', start: 'TSFP', end: 'TSElF', value: Number(patternData?.matchingSummary?.jacketSleeveTsFrontSeam_tsfpToTsElf || 0), stroke: '#7c3aed', offset: 0.34 },
                  { key: 'ts-tself-tshf', start: 'TSElF', end: 'TSHF', value: Number(patternData?.matchingSummary?.jacketSleeveTsFrontSeam_tsElfToTsHf || 0), stroke: '#7c3aed', offset: 0.34 },
                ].filter((segment) => namedPointByName[segment.start] && namedPointByName[segment.end]).map((segment) => {
                  const start = namedPointByName[segment.start];
                  const end = namedPointByName[segment.end];
                  const x1 = start.x;
                  const y1 = -start.y;
                  const x2 = end.x;
                  const y2 = -end.y;
                  const dx = x2 - x1;
                  const dy = y2 - y1;
                  const length = Math.hypot(dx, dy) || 1;
                  const nx = -dy / length;
                  const ny = dx / length;
                  const lineX1 = x1 + (nx * segment.offset);
                  const lineY1 = y1 + (ny * segment.offset);
                  const lineX2 = x2 + (nx * segment.offset);
                  const lineY2 = y2 + (ny * segment.offset);
                  let angle = (Math.atan2(dy, dx) * 180) / Math.PI;
                  if (angle > 90 || angle < -90) angle += 180;

                  return (
                    <g key={segment.key} data-testid={`jacket-sleeve-segment-${segment.key}`}>
                      <text
                        transform={`translate(${(lineX1 + lineX2) / 2} ${(lineY1 + lineY2) / 2}) rotate(${angle})`}
                        fontSize="0.4"
                        fontFamily="var(--font-mono)"
                        fill={segment.stroke}
                        fontWeight="600"
                        textAnchor="middle"
                        pointerEvents="none"
                        data-testid={`jacket-sleeve-segment-text-${segment.key}`}
                      >
                        {segment.value.toFixed(2)}&quot;
                      </text>
                    </g>
                  );
                })}

                {showMeasurements && patternData.garmentType === 'shirt' && patternData.matchingSummary?.totalArmholeHalf &&
                  patternData.frontMeasurements?.[0] && patternData.backMeasurements?.[0] && (
                    <text
                      x={((patternData.frontMeasurements[0].p1.x + patternData.frontMeasurements[0].p2.x + patternData.backMeasurements[0].p1.x + patternData.backMeasurements[0].p2.x) / 4)}
                      y={-((patternData.frontMeasurements[0].p1.y + patternData.frontMeasurements[0].p2.y + patternData.backMeasurements[0].p1.y + patternData.backMeasurements[0].p2.y) / 4) - 0.95}
                      fontSize="0.5"
                      fontFamily="var(--font-mono)"
                      fill="#0f172a"
                      fontWeight="500"
                      textAnchor="middle"
                      pointerEvents="none"
                      data-testid="shirt-total-armhole-half-canvas-text"
                    >
                      TAH: {Number(patternData.matchingSummary.totalArmholeHalf).toFixed(2)}"
                    </text>
                  )}

                {showMeasurements && patternData.garmentType === 'shirt' && shirtBackShoulderAngleGuide && (
                  <g data-testid="shirt-back-shoulder-angle-guide-group">
                    <line
                      x1={shirtBackShoulderAngleGuide.originSvg.x}
                      y1={shirtBackShoulderAngleGuide.originSvg.y}
                      x2={shirtBackShoulderAngleGuide.baseEnd.x}
                      y2={shirtBackShoulderAngleGuide.baseEnd.y}
                      stroke="#dc2626"
                      strokeWidth="0.12"
                      strokeDasharray="0.16 0.12"
                      opacity={0.85}
                      vectorEffect="non-scaling-stroke"
                      data-testid="shirt-back-shoulder-angle-horizontal-line"
                    />
                    <line
                      x1={shirtBackShoulderAngleGuide.originSvg.x}
                      y1={shirtBackShoulderAngleGuide.originSvg.y}
                      x2={shirtBackShoulderAngleGuide.shoulderEnd.x}
                      y2={shirtBackShoulderAngleGuide.shoulderEnd.y}
                      stroke="#b91c1c"
                      strokeWidth="0.14"
                      opacity={0.9}
                      vectorEffect="non-scaling-stroke"
                      data-testid="shirt-back-shoulder-angle-ray"
                    />
                    <text
                      x={shirtBackShoulderAngleGuide.labelPosition.x}
                      y={shirtBackShoulderAngleGuide.labelPosition.y}
                      fontSize="0.42"
                      fontFamily="var(--font-mono)"
                      fill="#991b1b"
                      fontWeight="600"
                      textAnchor="middle"
                      pointerEvents="none"
                      data-testid="shirt-back-shoulder-angle-canvas-text"
                    >
                      BSS: {Number(patternData?.matchingSummary?.backShoulderAngleDegrees || shirtBackShoulderAngleGuide.angleDegrees).toFixed(2)}°
                    </text>
                  </g>
                )}

                {showMeasurements && patternData.garmentType === 'shirt' && shirtFrontShoulderAngleGuide && (
                  <g data-testid="shirt-front-shoulder-angle-guide-group">
                    <line
                      x1={shirtFrontShoulderAngleGuide.originSvg.x}
                      y1={shirtFrontShoulderAngleGuide.originSvg.y}
                      x2={shirtFrontShoulderAngleGuide.baseEnd.x}
                      y2={shirtFrontShoulderAngleGuide.baseEnd.y}
                      stroke="#1d4ed8"
                      strokeWidth="0.12"
                      strokeDasharray="0.16 0.12"
                      opacity={0.85}
                      vectorEffect="non-scaling-stroke"
                      data-testid="shirt-front-shoulder-angle-horizontal-line"
                    />
                    <line
                      x1={shirtFrontShoulderAngleGuide.originSvg.x}
                      y1={shirtFrontShoulderAngleGuide.originSvg.y}
                      x2={shirtFrontShoulderAngleGuide.shoulderEnd.x}
                      y2={shirtFrontShoulderAngleGuide.shoulderEnd.y}
                      stroke="#1e40af"
                      strokeWidth="0.14"
                      opacity={0.9}
                      vectorEffect="non-scaling-stroke"
                      data-testid="shirt-front-shoulder-angle-ray"
                    />
                    <text
                      x={shirtFrontShoulderAngleGuide.labelPosition.x}
                      y={shirtFrontShoulderAngleGuide.labelPosition.y}
                      fontSize="0.42"
                      fontFamily="var(--font-mono)"
                      fill="#1e3a8a"
                      fontWeight="600"
                      textAnchor="middle"
                      pointerEvents="none"
                      data-testid="shirt-front-shoulder-angle-canvas-text"
                    >
                      FSS: {Number(patternData?.matchingSummary?.frontShoulderAngleDegrees || shirtFrontShoulderAngleGuide.angleDegrees).toFixed(2)}°
                    </text>
                  </g>
                )}

                {showMeasurements && patternData.garmentType === 'shirt' && patternData.matchingSummary?.backPleatApplied && (
                  <text
                    x={namedPointByName.CBY?.x || namedPointByName.BY?.x || 0}
                    y={-((namedPointByName.CBY?.y || namedPointByName.BY?.y || 0) + 0.95)}
                    fontSize="0.46"
                    fontFamily="var(--font-mono)"
                    fill="#0f172a"
                    fontWeight="500"
                    textAnchor="middle"
                    pointerEvents="none"
                    data-testid="shirt-back-pleat-drafted-measurement-text"
                  >
                    Pleat add: +{Number(patternData.matchingSummary.backPleatWidth || 0).toFixed(2)}" (half) / +{Number((patternData.matchingSummary.backPleatWidth || 0) * 2).toFixed(2)}" (full)
                  </text>
                )}

                {/* Total Crotch - positioned near FC & BC measurements */}
                {showMeasurements && patternData.grandTotalCrotch && patternData.frontMeasurements && patternData.backMeasurements && (
                  <text
                    x={(patternData.frontMeasurements[5].p1.x + patternData.frontMeasurements[5].p2.x) / 2 + 3}
                    y={-(patternData.frontMeasurements[5].p1.y + patternData.frontMeasurements[5].p2.y) / 2 - 1}
                    fontSize="0.9"
                    fontFamily="var(--font-mono)"
                    fill="#0f172a"
                    fontWeight="500"
                    textAnchor="middle"
                    pointerEvents="none"
                  >
                    TC: {patternData.grandTotalCrotch}"
                  </text>
                )}

                {Array.isArray(patternData.buttonholes) && patternData.buttonholes.filter(isHardwareVisible).map((hole) => (
                  <line
                    key={`buttonhole-${hole.id}`}
                    x1={hole.start.x}
                    y1={hole.start.y}
                    x2={hole.end.x}
                    y2={hole.end.y}
                    stroke="#111827"
                    strokeWidth="0.34"
                    vectorEffect="non-scaling-stroke"
                    data-testid={`canvas-${patternData.garmentType || 'garment'}-buttonhole-${hole.id}`}
                  />
                ))}

                {showMeasurements && patternData.garmentType === 'jacket' && patternData?.matchingSummary && (showFront || showBack || showSide) && (
                  <g data-testid="jacket-armhole-summary-group">
                    {[
                      { key: 'back-shoulder', start: 'BLPS', end: 'BACK_JOIN_NOTCH', value: Number(patternData.matchingSummary.sleeveBodyBackShoulderSegment || 0), color: '#991b1b', offset: -0.18, visible: showBack },
                      { key: 'back-side', start: 'BACK_JOIN_NOTCH', end: 'BAHP', value: Number(patternData.matchingSummary.sleeveBodyBackSideSegment || 0), color: '#991b1b', offset: -0.18, visible: showBack },
                      { key: 'side-arm', start: 'SAHP', end: 'SCPF', value: Number(patternData.matchingSummary.sideArmholeCurve || 0), color: '#c2410c', offset: 0.16, visible: showSide },
                      { key: 'front-side', start: 'FCPS', end: 'FRONT_JOIN_NOTCH', value: Number(patternData.matchingSummary.sleeveBodyFrontSideSegment || 0), color: '#0f766e', offset: 0.16, visible: showFront },
                      { key: 'front-shoulder', start: 'FRONT_JOIN_NOTCH', end: 'FLPS', value: Number(patternData.matchingSummary.sleeveBodyFrontShoulderSegment || 0), color: '#0f766e', offset: 0.16, visible: showFront },
                    ].filter((segment) => segment.visible && namedPointByName[segment.start] && namedPointByName[segment.end]).map((segment) => {
                      const start = namedPointByName[segment.start];
                      const end = namedPointByName[segment.end];
                      const x1 = start.x;
                      const y1 = -start.y;
                      const x2 = end.x;
                      const y2 = -end.y;
                      const dx = x2 - x1;
                      const dy = y2 - y1;
                      const length = Math.hypot(dx, dy) || 1;
                      const nx = -dy / length;
                      const ny = dx / length;
                      let angle = (Math.atan2(dy, dx) * 180) / Math.PI;
                      if (angle > 90 || angle < -90) angle += 180;
                      return (
                        <text
                          key={segment.key}
                          transform={`translate(${((x1 + x2) / 2) + (nx * segment.offset)} ${((y1 + y2) / 2) + (ny * segment.offset)}) rotate(${angle})`}
                          fontSize="0.38"
                          fontFamily="var(--font-mono)"
                          fill={segment.color}
                          fontWeight="600"
                          textAnchor="middle"
                          pointerEvents="none"
                          data-testid={`jacket-armhole-segment-${segment.key}-text`}
                        >
                          {segment.value.toFixed(2)}&quot;
                        </text>
                      );
                    })}

                    <text
                      x={(namedPointByName.FLPS && namedPointByName.BLPS)
                        ? ((namedPointByName.FLPS.x + namedPointByName.BLPS.x) / 2)
                        : ((patternData?.viewBox?.x || 0) + (patternData?.viewBox?.width || 40)) - 2.2}
                      y={(namedPointByName.FLPS && namedPointByName.BLPS)
                        ? -Math.min(namedPointByName.FLPS.y, namedPointByName.BLPS.y) - 0.95
                        : (-(patternData?.viewBox?.y || -30)) + 0.25}
                      fontSize="0.5"
                      fontFamily="var(--font-mono)"
                      fill="#16a34a"
                      fontWeight="700"
                      textAnchor="middle"
                      pointerEvents="none"
                      data-testid="jacket-total-armhole-canvas-text"
                    >
                      TAH: {Number(patternData.matchingSummary.totalArmholeCurve || 0).toFixed(2)}"
                    </text>
                  </g>
                )}

                {showMeasurements && patternData.garmentType === 'jacket' && patternData?.matchingSummary && (showFront || showBack) && (
                  <g data-testid="jacket-shoulder-summary-group">
                    {[
                      { key: 'back-shoulder-seam', start: 'BHPS', end: 'BLPS', value: Number(patternData.matchingSummary.backShoulderDrawn || 0), color: '#991b1b', offset: -0.16, visible: showBack },
                      { key: 'front-shoulder-seam', start: 'FHPS', end: 'FLPS', value: Number(patternData.matchingSummary.frontShoulderDrawn || 0), color: '#0f766e', offset: 0.16, visible: showFront },
                    ].filter((segment) => segment.visible && namedPointByName[segment.start] && namedPointByName[segment.end]).map((segment) => {
                      const start = namedPointByName[segment.start];
                      const end = namedPointByName[segment.end];
                      const x1 = start.x;
                      const y1 = -start.y;
                      const x2 = end.x;
                      const y2 = -end.y;
                      const dx = x2 - x1;
                      const dy = y2 - y1;
                      const length = Math.hypot(dx, dy) || 1;
                      const nx = -dy / length;
                      const ny = dx / length;
                      let angle = (Math.atan2(dy, dx) * 180) / Math.PI;
                      if (angle > 90 || angle < -90) angle += 180;
                      return (
                        <text
                          key={segment.key}
                          transform={`translate(${((x1 + x2) / 2) + (nx * segment.offset)} ${((y1 + y2) / 2) + (ny * segment.offset)}) rotate(${angle})`}
                          fontSize="0.38"
                          fontFamily="var(--font-mono)"
                          fill={segment.color}
                          fontWeight="600"
                          textAnchor="middle"
                          pointerEvents="none"
                          data-testid={`jacket-shoulder-segment-${segment.key}-text`}
                        >
                          {segment.value.toFixed(2)}&quot;
                        </text>
                      );
                    })}
                  </g>
                )}

                {showMeasurements && patternData.garmentType === 'jacket' && Array.isArray(patternData.jacketCollarNotes) && patternData.jacketCollarNotes
                  .filter((note) => isPieceVisible(note.piece || 'front'))
                  .map((note) => (
                  <text
                    key={note.id}
                    x={note.point.x}
                    y={note.point.y}
                    fontSize={note.fontSize || '0.42'}
                    fontFamily="var(--font-mono)"
                    fill={note.color || '#0f172a'}
                    fontWeight={note.fontWeight || '600'}
                    textAnchor={note.textAnchor || 'middle'}
                    pointerEvents="none"
                    data-testid={`canvas-jacket-collar-note-${note.id}`}
                  >
                    {note.text}
                  </text>
                ))}

                {Array.isArray(patternData.buttons) && patternData.buttons.filter(isHardwareVisible).map((button) => (
                  <g key={`button-${button.id}`} data-testid={`canvas-${patternData.garmentType || 'garment'}-button-${button.id}`}>
                    <circle
                      cx={button.x}
                      cy={button.y}
                      r="0.22"
                      fill={selectedPointNameSet.has(button.pointName) ? '#e0f2fe' : '#ffffff'}
                      stroke={selectedPointNameSet.has(button.pointName) ? '#0ea5e9' : '#111827'}
                      strokeWidth={selectedPointNameSet.has(button.pointName) ? '0.22' : '0.16'}
                      vectorEffect="non-scaling-stroke"
                    />
                    <circle
                      cx={button.x}
                      cy={button.y}
                      r="0.06"
                      fill={selectedPointNameSet.has(button.pointName) ? '#0369a1' : '#111827'}
                      stroke="none"
                    />
                  </g>
                ))}

                {Array.isArray(patternData?.curveSegments) && patternData.curveSegments
                  .filter((segment) => isCurveSegmentVisible(segment.id))
                  .map((segment) => (
                  <g key={`curve-selector-${segment.id}`} data-testid={`${garmentCurvePrefix}-curve-segment-${segment.id}`}>
                    <path
                      d={segment.d}
                      fill="none"
                      stroke={segment.guideOnly ? (segment.color || '#cbd5e1') : (selectedCurveId === segment.id ? (segment.color || '#0f172a') : '#94a3b8')}
                      strokeWidth={segment.strokeWidth || (segment.guideOnly ? '0.1' : (selectedCurveId === segment.id ? '0.2' : '0.12'))}
                      strokeDasharray={segment.strokeDasharray || undefined}
                      opacity={segment.opacity ?? (segment.guideOnly ? 0.7 : (selectedCurveId === segment.id ? 0.7 : 0.18))}
                      vectorEffect="non-scaling-stroke"
                      pointerEvents="none"
                    />
                    {!segment.guideOnly && (
                      <path
                        d={segment.d}
                        fill="none"
                        stroke="transparent"
                        strokeWidth={(
                          segment.id === 'front-side-seam-curve'
                          || segment.id === 'back-side-seam-curve'
                          || segment.id === 'side-front-seam-curve'
                          || segment.id === 'side-back-seam-curve'
                          || segment.id === 'front-hem-curve'
                        ) ? '7.5' : '3.8'}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        vectorEffect="non-scaling-stroke"
                        style={{ pointerEvents: 'stroke', cursor: 'pointer' }}
                        onMouseDown={(event) => handleCurveSegmentSelect(event, segment.id)}
                        onClick={(event) => handleCurveSegmentSelect(event, segment.id)}
                        data-testid={`${garmentCurvePrefix}-curve-segment-hitbox-${segment.id}`}
                      />
                    )}
                  </g>
                ))}

                {effectiveShowPoints && visibleNamedPointsList.length > 0 && visibleNamedPointsList
                  .filter((point) => isPieceVisible(point.piece))
                  .map((point) => {
                    const pointKey = `${point.piece}-${point.name}`;
                    const isFreeSelected = selectedPointNameSet.has(point.name);
                    return (
                      <g
                        key={pointKey}
                        data-testid={`${garmentCurvePrefix}-point-${point.name}`}
                      >
                        <circle
                          cx={point.x}
                          cy={-point.y}
                          r="0.2"
                          fill="transparent"
                          stroke="transparent"
                          onMouseDown={(event) => {
                            event.stopPropagation();
                          }}
                          onMouseEnter={() => setHoveredPointKey(pointKey)}
                          onMouseLeave={() => setHoveredPointKey((currentKey) => (currentKey === pointKey ? null : currentKey))}
                          onClick={(event) => {
                            event.stopPropagation();
                            if (!canEdit) return;
                            if (freeModeActive && point.selectable !== false) {
                              const nextSelection = new Set(selectedPointNameSet);
                              if (nextSelection.has(point.name)) {
                                nextSelection.delete(point.name);
                              } else {
                                nextSelection.add(point.name);
                              }
                              onFreePointSelectionChange?.(Array.from(nextSelection));
                              setPinnedPointKey(pointKey);
                              return;
                            }
                            setPinnedPointKey((currentKey) => (currentKey === pointKey ? null : pointKey));
                          }}
                          data-testid={`${garmentCurvePrefix}-point-hitbox-${point.name}`}
                          style={{ pointerEvents: canEdit ? 'all' : 'none', cursor: !canEdit ? 'default' : (freeModeActive && point.selectable === false ? 'help' : 'pointer') }}
                        />
                        {point.renderNode !== false && (
                          <circle
                            cx={point.x}
                            cy={-point.y}
                            r={isFreeSelected ? '0.16' : '0.1'}
                            fill={isFreeSelected ? '#0f172a' : getNamedPointColor(point.piece)}
                            stroke={isFreeSelected ? '#22d3ee' : 'none'}
                            strokeWidth={isFreeSelected ? '0.05' : '0'}
                            opacity={isFreeSelected ? 1 : 0.9}
                            className="cursor-pointer"
                            pointerEvents="none"
                            data-testid={`${garmentCurvePrefix}-point-node-${point.name}`}
                          />
                        )}
                        {point.renderNode !== false && showPointLabels && point.showLabel !== false && (
                          <text
                            x={point.x + (point.labelOffsetX ?? 0.14)}
                            y={-point.y + (point.labelOffsetY ?? -0.12)}
                            fontSize="0.5"
                            fill={getNamedPointColor(point.piece)}
                            fontWeight="500"
                            pointerEvents="none"
                            style={{ userSelect: 'none', WebkitUserSelect: 'none' }}
                            data-testid={`${garmentCurvePrefix}-point-label-${point.name}`}
                          >
                            {point.label || point.name}
                          </text>
                        )}
                      </g>
                    );
                  })}

                {effectiveShowPoints && pinnedPoint && renderPointTooltip(pinnedPoint, 'pinned')}
                {effectiveShowPoints && hoveredPoint && (!pinnedPoint || `${pinnedPoint.piece}-${pinnedPoint.name}` !== `${hoveredPoint.piece}-${hoveredPoint.name}`) && renderPointTooltip(hoveredPoint, 'hover')}

                {isLassoSelecting && lassoStart && lassoCurrent && (
                  <rect
                    x={Math.min(lassoStart.x, lassoCurrent.x)}
                    y={Math.min(lassoStart.y, lassoCurrent.y)}
                    width={Math.abs(lassoCurrent.x - lassoStart.x)}
                    height={Math.abs(lassoCurrent.y - lassoStart.y)}
                    fill="#0ea5e922"
                    stroke="#0ea5e9"
                    strokeWidth="0.08"
                    strokeDasharray="0.2 0.12"
                    vectorEffect="non-scaling-stroke"
                    pointerEvents="none"
                    data-testid={`${garmentCurvePrefix}-free-mode-lasso-rect`}
                  />
                )}

                {Array.isArray(patternData?.curveHandles) && patternData.curveHandles
                  .filter((handle) => handle.curveId === selectedCurveId)
                  .filter((handle) => isCurveSegmentVisible(handle.curveId || ''))
                  .map((handle) => (
                  <g
                    key={handle.id}
                    data-testid={`${garmentCurvePrefix}-curve-handle-${handle.id}`}
                    className={canEdit ? 'cursor-grab active:cursor-grabbing' : 'cursor-not-allowed'}
                  >
                    <polyline
                      points={`${handle.segmentStart.x},${-handle.segmentStart.y} ${handle.point.x},${-handle.point.y} ${handle.segmentEnd.x},${-handle.segmentEnd.y}`}
                      fill="none"
                      stroke={handle.color || '#0f172a'}
                      strokeWidth="0.08"
                      strokeDasharray="0.18 0.12"
                      opacity={0.55}
                      pointerEvents="none"
                    />
                    <circle
                      cx={handle.point.x}
                      cy={-handle.point.y}
                      r="1.05"
                      fill="transparent"
                      stroke="transparent"
                      style={{ pointerEvents: 'all' }}
                      onMouseDown={(event) => handleCurveHandleMouseDown(event, handle)}
                      data-testid={`${garmentCurvePrefix}-curve-handle-hitbox-${handle.id}`}
                    />
                    <circle
                      cx={handle.point.x}
                      cy={-handle.point.y}
                      r="0.22"
                      fill={activeCurveHandleId === handle.id ? '#fef3c7' : '#ffffff'}
                      stroke={handle.color || '#0f172a'}
                      strokeWidth={activeCurveHandleId === handle.id ? '0.12' : '0.09'}
                      className={canEdit ? 'cursor-grab active:cursor-grabbing' : 'cursor-not-allowed'}
                    />
                  </g>
                ))}
              </g>
            </svg>
          )}

          {patternData && !hasRenderablePattern && (
            <div
              className="rounded-md border bg-white/80 px-4 py-3 text-sm text-muted-foreground shadow"
              data-testid="canvas-empty-pattern-state"
            >
              {patternData.message || 'No drawable paths available for the selected garment yet.'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
